import { motion } from "framer-motion";
import { Briefcase, GraduationCap, FlaskConical } from "lucide-react";
import { timeline } from "@/data/portfolio";
import { Coursework } from "@/components/Coursework";

export const Timeline = () => {
  return (
    <section id="experience" className="relative py-24 lg:py-36" data-testid="timeline-section">
      <div className="pointer-events-none absolute left-0 top-1/4 h-[400px] w-[400px] rounded-full orb-violet" />
      <div className="relative mx-auto max-w-7xl px-4 sm:px-8 lg:px-16">
        <motion.div
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
        >
          <span className="font-mono text-xs uppercase tracking-[0.35em] text-cyan-400">
            {"// 05 — Trajectory"}
          </span>
          <h2 className="mt-4 font-display text-3xl font-bold tracking-tight text-slate-50 sm:text-4xl lg:text-5xl">
            Experience &amp; <span className="gradient-text">Education</span>
          </h2>
        </motion.div>

        <div className="relative mt-16 max-w-3xl">
          <div className="absolute left-[7px] top-2 h-[calc(100%-16px)] w-px bg-gradient-to-b from-cyan-400/60 via-violet-500/40 to-transparent" />
          <div className="space-y-12">
            {timeline.map((item, i) => (
              <motion.div
                key={item.title}
                initial={{ opacity: 0, x: -40 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true, margin: "-60px" }}
                transition={{ duration: 0.7, delay: i * 0.08, ease: [0.16, 1, 0.3, 1] }}
                className="relative pl-12"
                data-testid={`timeline-item-${i}`}
              >
                <span
                  className={`absolute left-0 top-1.5 h-[15px] w-[15px] rounded-full border-2 ${
                    item.type === "Education"
                      ? "border-cyan-400 bg-cyan-400/20 glow-cyan"
                      : item.type === "Experience"
                        ? "border-emerald-400 bg-emerald-400/20"
                        : "border-violet-500 bg-violet-500/20 glow-violet"
                  }`}
                />
                <div className="card-glass overflow-hidden rounded-xl border border-white/10 transition-all duration-300 hover:border-white/20">
                  {item.image && (
                    <div className="relative h-36 overflow-hidden">
                      <img
                        src={item.image}
                        alt={item.org}
                        loading="lazy"
                        className="h-full w-full object-cover transition-transform duration-700 hover:scale-105"
                        data-testid={`timeline-image-${i}`}
                      />
                      <div className="absolute inset-0 bg-gradient-to-t from-[#0D0E15]/70 via-transparent to-transparent" />
                    </div>
                  )}
                  <div className="p-6">
                  <div className="flex flex-wrap items-center justify-between gap-2">
                    <span className="font-mono text-[10px] uppercase tracking-widest text-slate-500">
                      {item.period}
                    </span>
                    <span className="flex items-center gap-1.5 font-mono text-[10px] uppercase tracking-widest text-slate-500">
                      {item.type === "Education" ? (
                        <GraduationCap size={12} className="text-cyan-400" />
                      ) : item.type === "Experience" ? (
                        <Briefcase size={12} className="text-emerald-400" />
                      ) : (
                        <FlaskConical size={12} className="text-violet-400" />
                      )}
                      {item.type}
                    </span>
                  </div>
                  <h3 className="mt-3 font-display text-lg font-bold text-slate-50 sm:text-xl">{item.title}</h3>
                  <p className="mt-1 font-mono text-xs text-cyan-400">{item.org}</p>
                  {item.story && (
                    <p
                      className="mt-3 border-l-2 border-violet-500/50 pl-3 text-sm italic leading-relaxed text-slate-300"
                      data-testid={`timeline-story-${i}`}
                    >
                      “{item.story}”
                    </p>
                  )}
                  <ul className="mt-4 space-y-2">
                    {item.points.map((pt) => (
                      <li key={pt} className="flex gap-2.5 text-sm leading-relaxed text-slate-400">
                        <span className="mt-2 h-1 w-1 shrink-0 rounded-full bg-violet-400" />
                        {pt}
                      </li>
                    ))}
                  </ul>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </div>
      <Coursework />
    </section>
  );
};
